import { useEffect, useState } from 'react';
import {
  X, RotateCcw, RotateCw, Download, Loader2, AlertCircle, ChevronLeft, ChevronRight,
  CheckCircle2, MessageSquare, Clock, Eye, RefreshCw, type LucideIcon,
} from 'lucide-react';
import { supabase, logAuditEvent } from '../lib/supabase';
import { driveSync } from '../lib/driveSync';

// Live uploads land in `intake-documents` (per uploadDocument.ts BUCKET_INTAKE).
// Pre-cutover demo data may live in `matter-documents`. Try the live bucket
// first, fall back to the legacy bucket so old previews still render.
const STORAGE_BUCKET = 'intake-documents';
const LEGACY_STORAGE_BUCKET = 'matter-documents';

async function createSignedUrlWithFallback(
  path: string,
  expiresIn = 600,
): Promise<{ signedUrl: string | null; bucket: string | null; error: string | null }> {
  const primary = await supabase.storage.from(STORAGE_BUCKET).createSignedUrl(path, expiresIn);
  if (primary.data?.signedUrl) {
    return { signedUrl: primary.data.signedUrl, bucket: STORAGE_BUCKET, error: null };
  }
  const fallback = await supabase.storage.from(LEGACY_STORAGE_BUCKET).createSignedUrl(path, expiresIn);
  if (fallback.data?.signedUrl) {
    return { signedUrl: fallback.data.signedUrl, bucket: LEGACY_STORAGE_BUCKET, error: null };
  }
  return {
    signedUrl: null,
    bucket: null,
    error: primary.error?.message ?? fallback.error?.message ?? 'Could not load preview.',
  };
}

export type ReviewStatus = 'pending' | 'reviewing' | 'approved' | 'needs_replacement';

export interface ViewerDoc {
  id: string;
  matter_id: string;
  storage_path: string;
  filename: string;
  mime_type?: string | null;
  size_bytes?: number | null;
  review_status?: ReviewStatus;
  review_note?: string | null;
  /**
   * Which side uploaded this doc. When 'sponsor', staff sees a "Sponsor
   * upload" badge in the header so they know which side it came from. Falls
   * back to deriving from storage_path (sponsor objects sit at
   * <matter_id>/sponsor/...) when not provided.
   */
  uploader_role?: 'applicant' | 'sponsor' | null;
}

export interface DocumentComment {
  id: string;
  body: string;
  created_at: string;
  review_status_at_post: ReviewStatus | null;
  author: { given_names: string | null; family_name: string | null } | null;
}

interface Props {
  doc: ViewerDoc | null;
  onClose: () => void;
  onUpdated?: (next: { id: string; storage_path: string; filename: string; mime_type: string }) => void;
  /** Position in the parent's doc list, for the "X of Y" caption + prev/next. */
  position?: { index: number; total: number };
  onPrev?: () => void;
  onNext?: () => void;
  /** Human label for the checklist slot this doc was uploaded against
   *  (e.g. "Applicant passport (bio page)"). Shown above the filename. */
  checklistLabel?: string | null;
  /** When true, show the staff review controls (status + note) in the footer. */
  showReviewControls?: boolean;
  /** Reviewing user's contact id, for audit log. */
  reviewerContactId?: string | null;
  /** Called when staff saves a status change so the parent can refresh state. */
  onReviewSaved?: (next: { id: string; review_status: ReviewStatus; review_note: string | null }) => void;
}

const IMAGE_MIME_PREFIX = 'image/';
const PDF_MIME = 'application/pdf';

export default function DocumentViewer({
  doc,
  onClose,
  onUpdated,
  position,
  onPrev,
  onNext,
  checklistLabel,
  showReviewControls,
  reviewerContactId,
  onReviewSaved,
}: Props) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [resolvedBucket, setResolvedBucket] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rotation, setRotation] = useState<0 | 90 | 180 | 270>(0);
  const [savingRotation, setSavingRotation] = useState(false);
  const [savedRotationToast, setSavedRotationToast] = useState(false);

  // Review state (staff-only). Status auto-saves on change; the note is a
  // separate independent action ("Post comment") that appends to the thread
  // and clears the textarea.
  const [reviewStatus, setReviewStatus] = useState<ReviewStatus>('pending');
  const [reviewNote, setReviewNote] = useState<string>('');
  const [savingReview, setSavingReview] = useState(false);
  // `emailFailed` marks a saved status change whose client notification email
  // did not send — surfaced as a distinct warning toast so staff know to
  // contact the client another way.
  const [lastToast, setLastToast] = useState<{ kind: 'status' | 'comment'; status: ReviewStatus; emailFailed?: boolean } | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);

  // Append-only feedback thread per document (staff → client).
  const [comments, setComments] = useState<DocumentComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);

  // Reset state when the doc changes.
  useEffect(() => {
    setSignedUrl(null);
    setError(null);
    setRotation(0);
    setSavedRotationToast(false);
    setLastToast(null);
    setReviewError(null);
    setReviewStatus(doc?.review_status ?? 'pending');
    // Note textarea always starts empty — it's for composing the next comment,
    // not editing the previous one. The thread below shows the history.
    setReviewNote('');
    setComments([]);
    if (!doc) return;
    // Load comment thread for this doc.
    (async () => {
      setCommentsLoading(true);
      const { data, error: cerr } = await supabase
        .from('document_comments')
        .select('id, body, created_at, review_status_at_post, author:contacts!document_comments_author_contact_id_fkey(given_names, family_name)')
        .eq('document_id', doc.id)
        .order('created_at', { ascending: true });
      if (!cerr && data) setComments(data as unknown as DocumentComment[]);
      setCommentsLoading(false);
    })();
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { signedUrl: url, bucket, error: err } = await createSignedUrlWithFallback(
        doc.storage_path,
      );
      if (cancelled) return;
      if (err || !url) {
        setError(err ?? 'Could not load preview.');
      } else {
        setSignedUrl(url);
        setResolvedBucket(bucket);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally keyed on the doc's stable identity fields, not the object
    // reference, so a new doc object with the same id/path does not reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.id, doc?.storage_path]);

  // Keyboard shortcuts: ESC closes; ← / → navigate when handlers are provided.
  useEffect(() => {
    if (!doc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowLeft' && onPrev) {
        e.preventDefault();
        onPrev();
      } else if (e.key === 'ArrowRight' && onNext) {
        e.preventDefault();
        onNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // Only the doc's presence/identity matters for binding the listener, keyed
    // via doc?.id; the full doc object reference is deliberately excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.id, onClose, onPrev, onNext]);

  if (!doc) return null;

  const mime = doc.mime_type ?? guessMime(doc.filename);
  const isImage = mime?.startsWith(IMAGE_MIME_PREFIX) ?? false;
  const isPdf = mime === PDF_MIME;
  // Sponsor-upload detection. Prefer the explicit column; fall back to the
  // storage path prefix so legacy callers that don't pass uploader_role still
  // surface the badge. Storage scheme: <matter_id>/sponsor/<contact_id>/...
  const isSponsorUpload =
    doc.uploader_role === 'sponsor' ||
    /^[0-9a-f-]{36}\/sponsor\//.test(doc.storage_path);

  const handleRotate = (delta: 90 | -90) => {
    setRotation((r) => normaliseRotation(r + delta));
  };

  const handleSaveRotation = async () => {
    if (!signedUrl || rotation === 0 || !isImage) return;
    setSavingRotation(true);
    try {
      const blob = await rotateImageBlob(signedUrl, rotation);
      // Replace the storage object at the same path. Re-encode as JPEG for
      // size, regardless of original. Write back to whichever bucket the
      // original came from (intake-documents for new uploads; matter-documents
      // for legacy files) so we don't accidentally fork into a different bucket.
      const writeBucket = resolvedBucket ?? STORAGE_BUCKET;
      const newPath = doc.storage_path.replace(/\.[a-zA-Z0-9]+$/, '') + '.jpg';
      const finalPath = newPath === doc.storage_path ? doc.storage_path : newPath;

      const { error: uploadErr } = await supabase.storage
        .from(writeBucket)
        .upload(finalPath, blob, {
          contentType: 'image/jpeg',
          upsert: true,
          cacheControl: '3600',
        });
      if (uploadErr) throw uploadErr;

      // If we changed the extension, also update the documents row with the new path + filename + mime.
      if (finalPath !== doc.storage_path) {
        const newFilename = doc.filename.replace(/\.[^.]+$/, '') + '.jpg';
        const { error: updErr } = await supabase
          .from('documents')
          .update({ storage_path: finalPath, filename: newFilename, mime_type: 'image/jpeg' })
          .eq('id', doc.id);
        if (updErr) throw updErr;
        // Best-effort cleanup of the old object.
        await supabase.storage.from(writeBucket).remove([doc.storage_path]).catch(() => {});
        onUpdated?.({ id: doc.id, storage_path: finalPath, filename: newFilename, mime_type: 'image/jpeg' });
      } else {
        const { error: updErr } = await supabase
          .from('documents')
          .update({ mime_type: 'image/jpeg' })
          .eq('id', doc.id);
        if (updErr) throw updErr;
        onUpdated?.({ id: doc.id, storage_path: doc.storage_path, filename: doc.filename, mime_type: 'image/jpeg' });
      }

      // Refresh signed URL so the rotated version loads.
      const { data: refresh } = await supabase.storage
        .from(writeBucket)
        .createSignedUrl(finalPath, 600);
      if (refresh?.signedUrl) setSignedUrl(refresh.signedUrl);
      setRotation(0);
      setSavedRotationToast(true);

      logAuditEvent('document.rotate_save', {
        tableName: 'documents',
        rowId: doc.id,
        changes: { rotation_applied: rotation },
      }).catch(() => {});
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save rotation.');
    } finally {
      setSavingRotation(false);
    }
  };

  // Status-only update. Fires when staff changes the dropdown — a status
  // change is not coupled to posting a note (the client may have already
  // replaced the file, etc.). Two separate actions so the UI doesn't imply
  // they belong together.
  const handleStatusChange = async (newStatus: ReviewStatus) => {
    if (!showReviewControls) return;
    setReviewStatus(newStatus);
    if (newStatus === (doc.review_status ?? 'pending')) return; // no-op
    setReviewError(null);
    setSavingReview(true);
    try {
      const { error: err } = await supabase
        .from('documents')
        .update({
          review_status: newStatus,
          reviewed_by: reviewerContactId ?? null,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', doc.id);
      if (err) throw err;
      logAuditEvent('document.review_saved', {
        tableName: 'documents',
        rowId: doc.id,
        changes: { review_status: newStatus },
      }).catch(() => {});

      // Approval gate (Batch 4C item 2, Jane 2026-07-21): approving a document
      // is what mirrors it to the ADI shared drive. Fire-and-forget; kick()
      // returns synchronously, swallows every error, and is a no-op unless
      // VITE_DRIVE_SYNC_ENABLED is set. The status write above is already
      // committed, so the sync function's own approval gate reads the row as
      // 'approved'. Nothing here can affect the status save.
      if (newStatus === 'approved') {
        driveSync.kick(doc.id);
      }

      // Queue the client notification for the END-OF-DAY digest (Tzvi/Jane
      // ruling 2026-07-20: one summary email per day, not one per change).
      // `approved` and `needs_replacement` are the only client-visible
      // transitions; the others are internal triage states. A failed queue
      // insert doesn't block the save (it already persisted above), but it
      // must NOT be silent: staff get a distinct warning toast so they can
      // contact the client another way.
      let emailFailed = false;
      if (newStatus === 'approved' || newStatus === 'needs_replacement') {
        emailFailed = true; // pessimistic until the insert confirms
        try {
          const { error: outboxErr } = await supabase
            .from('notification_outbox' as never)
            .insert({
              document_id: doc.id,
              event_type: newStatus === 'approved' ? 'doc_approved' : 'doc_needs_replacement',
              // Human checklist label so the email never shows a raw key.
              checklist_label: checklistLabel ?? null,
            } as never);
          emailFailed = !!outboxErr;
          if (outboxErr) console.warn('digest queue insert failed', outboxErr);
        } catch (queueErr) {
          console.warn('digest queue insert failed', queueErr);
        }
      }

      setLastToast({ kind: 'status', status: newStatus, emailFailed });
      window.setTimeout(() => setLastToast(null), emailFailed ? 8000 : 4000);
      onReviewSaved?.({ id: doc.id, review_status: newStatus, review_note: doc.review_note ?? null });
    } catch (e) {
      setReviewError(e instanceof Error ? e.message : 'Failed to update status.');
    } finally {
      setSavingReview(false);
    }
  };

  // Independent comment post — staff can leave any number of notes without
  // touching the status. Inserts a `document_comments` row, refreshes the
  // thread, and clears the textarea so they can keep typing the next message.
  const handlePostComment = async () => {
    if (!showReviewControls) return;
    const body = reviewNote.trim();
    if (!body) {
      setReviewError('Type a note before posting.');
      return;
    }
    if (!reviewerContactId) {
      setReviewError('Could not identify your staff account. Try refreshing.');
      return;
    }
    setReviewError(null);
    setSavingReview(true);
    try {
      const { data: insertedComment, error: cerr } = await supabase
        .from('document_comments')
        .insert({
          document_id: doc.id,
          matter_id: doc.matter_id,
          author_contact_id: reviewerContactId,
          body,
          review_status_at_post: reviewStatus,
        })
        .select('id, body, created_at, review_status_at_post, author:contacts!document_comments_author_contact_id_fkey(given_names, family_name)')
        .single();
      if (cerr) throw cerr;
      if (insertedComment) {
        setComments((prev) => [...prev, insertedComment as unknown as DocumentComment]);
      }
      logAuditEvent('document.comment_posted', {
        tableName: 'documents',
        rowId: doc.id,
        changes: { review_status_at_post: reviewStatus },
      }).catch(() => {});
      setReviewNote(''); // reset so the next comment can be typed
      setLastToast({ kind: 'comment', status: reviewStatus });
      window.setTimeout(() => setLastToast(null), 4000);
    } catch (e) {
      setReviewError(e instanceof Error ? e.message : 'Failed to post note.');
    } finally {
      setSavingReview(false);
    }
  };

  const showRightPane = showReviewControls || comments.length > 0 || commentsLoading;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-2 sm:p-4 bg-black/70" onClick={onClose}>
      <div
        className="relative bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[94vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header (full width) */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200">
          <div className="min-w-0 flex-1">
            {checklistLabel && (
              <div className="text-base font-semibold text-[#0B2341] truncate flex items-center gap-2 flex-wrap">
                {checklistLabel}
                <StatusBadge status={reviewStatus} />
                {isSponsorUpload && (
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold text-purple-800 bg-purple-50 border border-purple-200"
                    title="Uploaded by the sponsor side of this matter"
                  >
                    Sponsor upload
                  </span>
                )}
              </div>
            )}
            <div className="text-xs text-slate-500 truncate" title={doc.filename}>
              {doc.filename}
            </div>
            <div className="text-[11px] text-slate-400">
              {mime ?? 'unknown type'}
              {doc.size_bytes ? ` · ${formatBytes(doc.size_bytes)}` : ''}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {isImage && (
              <>
                <button type="button" onClick={() => handleRotate(-90)} className="p-2 rounded-md hover:bg-slate-100" title="Rotate 90° anticlockwise">
                  <RotateCcw size={16} />
                </button>
                <button type="button" onClick={() => handleRotate(90)} className="p-2 rounded-md hover:bg-slate-100" title="Rotate 90° clockwise">
                  <RotateCw size={16} />
                </button>
                {rotation !== 0 && (
                  <button type="button" onClick={handleSaveRotation} disabled={savingRotation}
                    className="ml-1 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50">
                    {savingRotation ? <Loader2 size={12} className="animate-spin" /> : null}
                    Save rotation
                  </button>
                )}
              </>
            )}
            {signedUrl && (
              <a href={signedUrl} download={doc.filename} className="p-2 rounded-md hover:bg-slate-100" title="Download">
                <Download size={16} />
              </a>
            )}
            <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-slate-100" title="Close">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body — side-by-side on xl+, stacked below */}
        <div className={`flex-1 min-h-0 overflow-hidden flex ${showRightPane ? 'flex-col xl:flex-row' : 'flex-col'}`}>
          {/* LEFT — document preview */}
          <div className={`bg-slate-50 grid place-items-center overflow-auto min-h-[40vh] ${showRightPane ? 'xl:flex-1 xl:min-h-0' : 'flex-1'}`}>
            {loading && (
              <div className="text-sm text-slate-500 inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Loading preview…
              </div>
            )}
            {error && (
              <div className="text-sm text-red-600 inline-flex items-center gap-2 px-4 text-center">
                <AlertCircle size={14} /> {error}
              </div>
            )}
            {!loading && !error && signedUrl && (
              <>
                {isImage && (
                  <img src={signedUrl} alt={doc.filename}
                    className="max-w-full max-h-[80vh] transition-transform"
                    style={{ transform: `rotate(${rotation}deg)` }} />
                )}
                {isPdf && (
                  <iframe src={signedUrl} title={doc.filename}
                    className="w-full h-full min-h-[60vh] border-0 bg-white" />
                )}
                {!isImage && !isPdf && (
                  <div className="text-sm text-slate-600 text-center max-w-md p-6">
                    <p className="mb-3">In-portal preview isn't supported for this file type ({mime ?? 'unknown'}).</p>
                    <a href={signedUrl} download={doc.filename} className="btn-primary inline-flex">
                      <Download size={14} /> Download to view
                    </a>
                  </div>
                )}
              </>
            )}
          </div>

          {/* RIGHT pane — three vertically-stacked regions, in this order:
                1. Note composer  (top, sticky-shrink)
                2. Feedback timeline (middle, scrolls — only this region scrolls)
                3. Status         (bottom, sticky-shrink, near the prev/next nav)

              Layout uses an explicit max-height (calc against the viewport) so
              the timeline always has a bounded scroll container regardless of
              how many comments are in the thread. */}
          {showRightPane && (
            <div
              className="border-t xl:border-t-0 xl:border-l border-slate-200 bg-white xl:w-[420px] flex flex-col overflow-hidden shrink-0"
              style={{ maxHeight: 'calc(94vh - 60px)' }}
            >
              {/* 1. NOTE COMPOSER (top) */}
              {showReviewControls && (
                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 shrink-0 space-y-2">
                  <label className="block text-xs font-semibold text-slate-600">
                    Add a note for the client
                  </label>
                  <textarea
                    value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                    rows={3}
                    placeholder="e.g. Wrong page, please send page 2 of the passport."
                    className="w-full text-sm rounded-md border border-slate-300 bg-white px-2 py-2 resize-none"
                  />
                  <button
                    type="button"
                    onClick={handlePostComment}
                    disabled={!reviewNote.trim() || savingReview}
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-[#0B2341] text-white text-sm font-semibold hover:bg-[#0B2341]/90 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {savingReview ? <Loader2 size={14} className="animate-spin" /> : <MessageSquare size={14} />}
                    Post comment
                  </button>
                  {reviewError && (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{reviewError}</div>
                  )}
                </div>
              )}

              {/* 2. FEEDBACK TIMELINE (middle, only-scrollable region) */}
              <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 bg-white">
                <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wide font-semibold text-slate-500 sticky top-0 bg-white pb-1 -mt-1 pt-1">
                  <MessageSquare size={12} /> Feedback timeline ({comments.length})
                </div>
                {commentsLoading ? (
                  <div className="text-xs text-slate-400 flex items-center gap-1.5">
                    <Loader2 size={12} className="animate-spin" /> Loading…
                  </div>
                ) : comments.length === 0 ? (
                  <div className="text-xs text-slate-400 italic">No feedback yet.</div>
                ) : (
                  <ol className="space-y-2">
                    {comments.map((c) => <CommentCard key={c.id} comment={c} />)}
                  </ol>
                )}
              </div>

              {/* 3. STATUS (bottom, near nav) */}
              {showReviewControls && (
                <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 shrink-0">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-semibold text-slate-600">Status</label>
                    <span className="text-[10px] text-slate-400">Saves automatically</span>
                  </div>
                  <select
                    value={reviewStatus}
                    onChange={(e) => handleStatusChange(e.target.value as ReviewStatus)}
                    disabled={savingReview}
                    className="w-full text-sm rounded-md border border-slate-300 bg-white px-2 py-2 disabled:opacity-60"
                  >
                    <option value="pending">Pending review</option>
                    <option value="reviewing">Reviewing</option>
                    <option value="approved">Approved</option>
                    <option value="needs_replacement">Needs replacement</option>
                  </select>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Prev / next nav (full width footer) */}
        {(position || onPrev || onNext) && (
          <div className="flex items-center justify-between gap-3 px-4 py-2 border-t border-slate-200 text-sm shrink-0">
            <button type="button" onClick={onPrev} disabled={!onPrev}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Previous document (←)">
              <ChevronLeft size={14} /> Prev
            </button>
            {position && (
              <div className="text-xs text-slate-500">
                {position.index + 1} of {position.total} on this matter
                <span className="ml-3 text-slate-400 hidden sm:inline">Use ← → keys, ESC to close</span>
              </div>
            )}
            <button type="button" onClick={onNext} disabled={!onNext}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Next document (→)">
              Next <ChevronRight size={14} />
            </button>
          </div>
        )}

        {/* Floating toast — overlays the modal, top-centre, large + animated.
            One handler covers the status-change and post-comment actions.
            Amber variant = status saved but the client email did NOT send, so
            staff know to reach the client another way. */}
        {lastToast && (
          lastToast.kind === 'status' && lastToast.emailFailed ? (
            <div
              role="status"
              aria-live="polite"
              className="absolute top-4 left-1/2 -translate-x-1/2 z-10 max-w-md w-[calc(100%-2rem)] rounded-lg shadow-lg border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3 animate-in fade-in slide-in-from-top-2"
            >
              <AlertCircle size={22} className="text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-amber-900">
                  Status saved, but the client notification was not queued
                </div>
                <div className="text-sm text-amber-800">
                  Status set to <span className="font-semibold">{statusMeta(lastToast.status).label}</span>, but
                  it could not be added to today's summary email to the client. Please contact them another way.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setLastToast(null)}
                className="p-1 rounded hover:bg-amber-100 text-amber-700"
                aria-label="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <div
              role="status"
              aria-live="polite"
              className="absolute top-4 left-1/2 -translate-x-1/2 z-10 max-w-md w-[calc(100%-2rem)] rounded-lg shadow-lg border border-green-200 bg-green-50 px-4 py-3 flex items-start gap-3 animate-in fade-in slide-in-from-top-2"
            >
              <CheckCircle2 size={22} className="text-green-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-green-900">
                  {lastToast.kind === 'status' ? 'Status updated' : 'Comment posted'}
                </div>
                <div className="text-sm text-green-800">
                  {lastToast.kind === 'status'
                    ? <>
                        Status set to <span className="font-semibold">{statusMeta(lastToast.status).label}</span>.
                        {(lastToast.status === 'approved' || lastToast.status === 'needs_replacement') &&
                          ' The client will see this in today’s summary email.'}
                      </>
                    : 'Your note is now visible to the client in the feedback timeline.'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setLastToast(null)}
                className="p-1 rounded hover:bg-green-100 text-green-700"
                aria-label="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          )
        )}

        {/* Rotation toast */}
        {savedRotationToast && (
          <div className="px-4 py-2 text-xs text-green-700 bg-green-50 border-t border-green-200 shrink-0">
            Rotation saved. The new orientation is now permanent.
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status helpers (icons + labels reused everywhere status appears)
// ---------------------------------------------------------------------------

interface StatusMeta {
  label: string;
  Icon: LucideIcon;
  color: string;       // text colour
  bg: string;          // background tint for pill/badge
  border: string;      // border colour for cards
}

export function statusMeta(s: ReviewStatus): StatusMeta {
  switch (s) {
    case 'pending':
      return { label: 'Pending review', Icon: Clock, color: 'text-slate-600', bg: 'bg-slate-100', border: 'border-slate-200' };
    case 'reviewing':
      return { label: 'Reviewing', Icon: Eye, color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' };
    case 'approved':
      return { label: 'Approved', Icon: CheckCircle2, color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' };
    case 'needs_replacement':
      return { label: 'Needs replacement', Icon: RefreshCw, color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' };
  }
}

function StatusBadge({ status }: { status: ReviewStatus }) {
  const meta = statusMeta(status);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta.color} ${meta.bg}`}>
      <meta.Icon size={11} /> {meta.label}
    </span>
  );
}

function CommentCard({ comment }: { comment: DocumentComment }) {
  const author = [comment.author?.given_names, comment.author?.family_name].filter(Boolean).join(' ').trim() || 'Your case manager';
  const dt = new Date(comment.created_at);
  const status = (comment.review_status_at_post ?? 'pending') as ReviewStatus;
  const meta = statusMeta(status);
  return (
    <li className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800">
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${meta.color} ${meta.bg}`}>
          <meta.Icon size={10} /> {meta.label}
        </span>
        <span className="text-xs font-semibold text-[#0B2341] truncate">{author}</span>
        <span className="ml-auto text-[10px] text-slate-400 shrink-0">
          {dt.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
      <p className="whitespace-pre-wrap leading-relaxed">{comment.body}</p>
    </li>
  );
}

function normaliseRotation(deg: number): 0 | 90 | 180 | 270 {
  const r = ((deg % 360) + 360) % 360;
  if (r === 0 || r === 90 || r === 180 || r === 270) return r as 0 | 90 | 180 | 270;
  // Snap to nearest cardinal.
  return [0, 90, 180, 270].reduce((best, candidate) =>
    Math.abs(candidate - r) < Math.abs(best - r) ? candidate : best,
  0) as 0 | 90 | 180 | 270;
}

function guessMime(filename: string): string | null {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf') return 'application/pdf';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'png') return 'image/png';
  if (ext === 'heic' || ext === 'heif') return 'image/heic';
  return null;
}

function formatBytes(bytes: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Load a remote image, render it rotated onto a canvas, return a JPEG blob.
 * Server CORS must allow image loading; Supabase signed URLs do.
 */
async function rotateImageBlob(srcUrl: string, deg: 0 | 90 | 180 | 270): Promise<Blob> {
  const img = await loadImage(srcUrl);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported in this browser.');
  if (deg === 90 || deg === 270) {
    canvas.width = img.naturalHeight;
    canvas.height = img.naturalWidth;
  } else {
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
  }
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((deg * Math.PI) / 180);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Failed to encode rotated image.'))),
      'image/jpeg',
      0.92,
    );
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load image.'));
    img.src = src;
  });
}
